import React, { useMemo, useState } from "react";
import { Box, Grid, HStack, Image, Stack, Text } from "@chakra-ui/react";
import { DashboardInput } from "components/utils/FormControls";
import { MdLocationPin, MdMap } from "react-icons/md";
import { FaPersonFalling, FaPersonRunning } from "react-icons/fa6";
import { DataCard } from "components/utils/DataCard";
import { FormDialog } from "components/utils/FormDialog";
import { toNullIfEmpty } from "utils/formatters";
import { LugarCombobox } from "../components/LugarCombobox";
import { useDialogForm } from "utils/useDialogForm";

function TransferInput({ icon, label, value, onChange }) {
  return (
    <Stack gap="2">
      <HStack gap="2" align="center">
        <Box color="dashboard.fg.muted" flexShrink={0}>{icon}</Box>
        <Text fontSize="xs" color="dashboard.fg.muted" textTransform="uppercase" fontWeight="700" letterSpacing="0.06em">
          {label}
        </Text>
      </HStack>
      <DashboardInput h="10" value={value} onChange={onChange} />
    </Stack>
  );
}

export const UbicacionCard = ({ actividad, actividadId, patch, lugares }) => {
  const dialog = useDialogForm({
    lugarActividadId: "",
    dropOffOther: "",
    pickUpOther: "",
  });
  const [advancedOpen, setAdvancedOpen] = useState(false);

  const saving = patch.isPending;
  const patchError = patch.isError ? String(patch.error?.message || patch.error) : null;

  const selectedLugar = useMemo(() => {
    const id = actividad?.lugarActividadId;
    if (!id) return null;
    return (lugares ?? []).find((lugar) => lugar.id === id) ?? null;
  }, [actividad?.lugarActividadId, lugares]);

  const dialogLugar = useMemo(() => {
    const id = dialog.form.lugarActividadId;
    if (!id) return null;
    return (lugares ?? []).find((lugar) => lugar.id === id) ?? null;
  }, [dialog.form.lugarActividadId, lugares]);

  const lugarTxt = selectedLugar?.nombre ?? "Sin lugar";
  const dropOffRaw = actividad?.dropOff?.trim() || "";
  const pickUpRaw = actividad?.pickUp?.trim() || "";
  const samePickDropView = !dropOffRaw || !pickUpRaw || dropOffRaw === pickUpRaw;
  const trasladoTxt = dropOffRaw || pickUpRaw || "";
  const dropOffTxt = dropOffRaw || "";
  const pickUpTxt = pickUpRaw || "";

  function openDialog() {
    const pickUp = actividad?.pickUp ?? "";
    const dropOff = actividad?.dropOff ?? "";
    const pickUpTrim = pickUp.trim();
    const dropOffTrim = dropOff.trim();
    const lugarName = selectedLugar?.nombre?.trim() ?? "";

    setAdvancedOpen(false);

    dialog.openWith({
      lugarActividadId: actividad?.lugarActividadId ?? "",
      dropOffOther: dropOffTrim || lugarName,
      pickUpOther: pickUpTrim || dropOffTrim || lugarName,
    });
  }

  function handleLugarChange(lugarId) {
    const oldLugarName = dialogLugar?.nombre?.trim() ?? "";
    const newLugar = (lugares ?? []).find((lugar) => lugar.id === lugarId) ?? null;
    const newLugarName = newLugar?.nombre?.trim() ?? "";

    dialog.setForm((prev) => {
      const shouldSync = (value) => {
        const trimmed = String(value ?? "").trim();
        return !trimmed || (oldLugarName && trimmed === oldLugarName);
      };

      return {
        ...prev,
        lugarActividadId: lugarId,
        dropOffOther: shouldSync(prev.dropOffOther) ? newLugarName : prev.dropOffOther,
        pickUpOther: shouldSync(prev.pickUpOther) ? newLugarName : prev.pickUpOther,
      };
    });
  }

  async function save() {
    await patch.mutateAsync({
      actividadId,
      patch: {
        lugarActividadId: toNullIfEmpty(dialog.form.lugarActividadId),
        dropOff: toNullIfEmpty(dialog.form.dropOffOther),
        pickUp: toNullIfEmpty(dialog.form.pickUpOther),
      },
    });
    dialog.close();
  }

  return (
    <>
      <DataCard title="Ubicacion" variant="green" headerIcon={<MdMap size="24px" />} onEdit={openDialog} editAriaLabel="Editar ubicacion">
        <Stack gap="4" alignItems="start">
          <HStack
            gap="3"
            w="full"
            align="center"
            rounded="xl"
            bg="dashboard.bg.surfaceMuted"
            borderWidth="1px"
            borderColor="dashboard.border.muted"
            p="2.5"
          >
            {selectedLugar?.googlePhotoUri ? (
              <Image
                src={selectedLugar.googlePhotoUri}
                alt={selectedLugar.nombre}
                boxSize="56px"
                rounded="lg"
                objectFit="cover"
                bg="dashboard.bg.subtle"
                flexShrink={0}
              />
            ) : (
              <Box
                boxSize="56px"
                rounded="lg"
                bg="dashboard.bg.choiceSelected"
                color="dashboard.fg.muted"
                display="flex"
                alignItems="center"
                justifyContent="center"
                flexShrink={0}
              >
                <MdLocationPin size="22px" />
              </Box>
            )}
            <Stack gap="0" minW={0}>
              <Text fontSize="xs" color="dashboard.fg.muted" textTransform="uppercase" fontWeight="700" letterSpacing="0.06em">Lugar de la Actividad</Text>
              <Text fontWeight="800" fontSize="sm" color="dashboard.fg.default" lineHeight="1.25" lineClamp={2}>{lugarTxt}</Text>
            </Stack>
          </HStack>

          {samePickDropView ? (
            trasladoTxt ? (
              <HStack gap="2.5" align="start">
                <Box color="dashboard.fg.muted" mt="0.5" flexShrink={0}><MdLocationPin size="16px" /></Box>
                <Stack gap="0">
                  <Text fontSize="xs" color="dashboard.fg.muted" textTransform="uppercase" fontWeight="700" letterSpacing="0.06em">Drop-off y Pick-up</Text>
                  <Text fontWeight="600" fontSize="sm" color="dashboard.fg.default">{trasladoTxt}</Text>
                </Stack>
              </HStack>
            ) : null
          ) : (
            <Grid templateColumns="repeat(2, minmax(0, 1fr))" gap="3" alignItems="start" w="full">
              {dropOffTxt && (
                <HStack gap="2.5" align="start">
                  <Box color="dashboard.fg.muted" mt="0.5" flexShrink={0}><FaPersonFalling size="16px" /></Box>
                  <Stack gap="0" minW={0}>
                    <Text fontSize="xs" color="dashboard.fg.muted" textTransform="uppercase" fontWeight="700" letterSpacing="0.06em">Drop-off</Text>
                    <Text fontWeight="600" fontSize="sm" color="dashboard.fg.default">{dropOffTxt}</Text>
                  </Stack>
                </HStack>
              )}

              {pickUpTxt && (
                <HStack gap="2.5" align="start">
                  <Box color="dashboard.fg.muted" mt="0.5" flexShrink={0}><FaPersonRunning size="16px" /></Box>
                  <Stack gap="0" minW={0}>
                    <Text fontSize="xs" color="dashboard.fg.muted" textTransform="uppercase" fontWeight="700" letterSpacing="0.06em">Pick-up</Text>
                    <Text fontWeight="600" fontSize="sm" color="dashboard.fg.default">{pickUpTxt}</Text>
                  </Stack>
                </HStack>
              )}
            </Grid>
          )}
        </Stack>
      </DataCard>

      <FormDialog open={dialog.open} onOpenChange={dialog.onOpenChange} title="Editar Ubicacion" errorText={patchError} saving={saving} onCancel={dialog.close} onSave={save}>
        <Stack gap="5">
          <Box>
            <Text fontSize="xs" color="dashboard.fg.muted" textTransform="uppercase" fontWeight="700" letterSpacing="0.06em" mb="2">
              Lugar
            </Text>
            <LugarCombobox
              items={(lugares ?? []).map((l) => ({ label: l.nombre, value: l.id }))}
              label=""
              value={dialog.form.lugarActividadId}
              onChange={handleLugarChange}
            />
          </Box>

          <HStack
            gap="3"
            align="center"
            rounded="xl"
            bg="dashboard.bg.surfaceMuted"
            borderWidth="1px"
            borderColor="dashboard.border.muted"
            p="2.5"
          >
            {dialogLugar?.googlePhotoUri ? (
              <Image
                src={dialogLugar.googlePhotoUri}
                alt={dialogLugar.nombre}
                boxSize="52px"
                rounded="lg"
                objectFit="cover"
                bg="dashboard.bg.subtle"
                flexShrink={0}
              />
            ) : (
              <Box
                boxSize="52px"
                rounded="lg"
                bg="dashboard.bg.choiceSelected"
                color="dashboard.fg.muted"
                display="flex"
                alignItems="center"
                justifyContent="center"
                flexShrink={0}
              >
                <MdLocationPin size="22px" />
              </Box>
            )}
            <Stack gap="0" minW={0}>
              <Text fontSize="xs" color="dashboard.fg.muted" textTransform="uppercase" fontWeight="700" letterSpacing="0.06em">
                Seleccionado
              </Text>
              <Text fontWeight="800" fontSize="sm" color="dashboard.fg.default" lineHeight="1.25" lineClamp={2}>
                {dialogLugar?.nombre ?? "Sin lugar"}
              </Text>
            </Stack>
          </HStack>

          <Box
            as="button"
            type="button"
            alignSelf="start"
            color="dashboard.fg.muted"
            fontSize="xs"
            fontWeight="700"
            textDecoration="underline"
            textUnderlineOffset="3px"
            onClick={() => setAdvancedOpen((open) => !open)}
            _hover={{ color: "dashboard.accent.fg" }}
            _focusVisible={{ outline: "2px solid", outlineColor: "dashboard.accent.border", outlineOffset: "2px" }}
          >
            {advancedOpen ? "Ocultar configuracion avanzada" : "Configuracion avanzada: personaliza el drop-off y pick-up"}
          </Box>

          {advancedOpen && (
          <Box rounded="lg" borderWidth="1px" borderColor="dashboard.border.muted" p="3">
            <Grid templateColumns={{ base: "1fr", sm: "repeat(2, minmax(0, 1fr))" }} gap="3">
              <TransferInput
                icon={<FaPersonFalling size="15px" />}
                label="Drop-off"
                value={dialog.form.dropOffOther}
                onChange={(e) => dialog.setField("dropOffOther", e.target.value)}
              />
              <TransferInput
                icon={<FaPersonRunning size="15px" />}
                label="Pick-up"
                value={dialog.form.pickUpOther}
                onChange={(e) => dialog.setField("pickUpOther", e.target.value)}
              />
            </Grid>
          </Box>
          )}
        </Stack>
      </FormDialog>
    </>
  );
};
